import React from 'react';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { ActionsManager } from './actionsManager/actionsManager';
import { Designer as DesignerLegacy } from 'controls/designer/designer';
import { DesignerConfig, DesignerData } from 'controls/designer/designerConfig';
import { Settings } from './settings';
import { MobxManager } from 'framework/mobx-integration';
import {
	isMxChildChange,
	MxCell,
	MxClipboard,
	MxEvent,
	MxEventObject,
	MxGraphModel,
} from 'controls/designer/mxGraphInterfaces';
import { DataSourcesManager } from 'controls/designer/dataSourcesManager/dataSourcesManager';
import { RedirectConfigsManager } from 'controls/designer/features/redirectOnClick/redirectConfigsManager';
import { WidgetsManager } from 'controls/designer/features/widgets/widgetsManager';
import { newGuid } from 'tools/guid';
import { CustomDataEntry } from 'controls/designer/customDataEntry';
import { iterateAllCells, iterateChildren } from 'controls/designer/utils';
import { serialize } from 'serializr';
import { copyViaSerializr } from 'framework/serializr-integration';
import { WidgetWizardStore } from 'controls/designer/features/widgets/wizard/widgetWizardStore';
import { ArrayHelpers } from 'tools/helpers';
import { getUrlBuilderForCurrentHash } from 'tools/urlBuilder';
import { NavigationAction, NavigationStore } from 'framework/navigationStore';
import { createContainer } from 'controls/react/ant/antModal';
import { AntAlert, AntAlertProps } from 'controls/react/ant/antAlert';
import { ViewManager } from 'controls/designer/viewManager/viewManager';
import { GraphManager } from 'controls/designer/graphManager';
import { GlobalDashboardTimeSelectorSettings } from 'controls/designer/features/widgets/globalTimeSelector/store';

export const DesignerContext = React.createContext<DesignerStore>(null);

export class ToolbarFeature {
	store: DesignerStore;

	enabled: boolean = false;
	items: string;

	constructor(store: DesignerStore) {
		this.store = store;

		this.items = store.config.toolbar ?? '';
	}
}

export class DesignerStore {
	legacyDesigner: DesignerLegacy;

	config: DesignerConfig;

	toolbar: ToolbarFeature;

	scale = 100;
	settings: Settings;

	am: ActionsManager;

	featuresLoaded: boolean = false;

	resetWindowsPositionsTrigger: string;

	customData: CustomDataEntry[] = [];
	deletedCustomData: CustomDataEntry[] = [];

	dataSourcesManager: DataSourcesManager;
	redirectConfigsManager: RedirectConfigsManager;
	widgetsManager: WidgetsManager;
	viewManager: ViewManager;
	graphManager: GraphManager;

	widgetWizardStore: WidgetWizardStore;
	timeSettings: GlobalDashboardTimeSelectorSettings;

	bypassDirtyCheck: boolean;

	data: DesignerData;

	hideTempObjects: boolean;

	get graph() {
		return this.legacyDesigner.graph;
	}

	get editor() {
		return this.legacyDesigner.editor;
	}

	get ui() {
		return this.legacyDesigner.ui;
	}

	get viewSettings() {
		return this.data.viewSettings;
	}

	cell: MxCell;
	cells: MxCell[] = [];

	mobx = new MobxManager();

	constructor(designer: DesignerLegacy) {
		this.legacyDesigner = designer;
		this.config = designer.config;
		this.dataSourcesManager = new DataSourcesManager(this);
		this.redirectConfigsManager = new RedirectConfigsManager(this);
		this.widgetsManager = new WidgetsManager(this);
		this.viewManager = new ViewManager(this);
		this.graphManager = new GraphManager(this);

		if (!this.config.chromeless) {
			this.widgetWizardStore = new WidgetWizardStore(this);
		}

		this.am = new ActionsManager(this);
		this.toolbar = new ToolbarFeature(this);

		this.initializeDesignerData();
		this.viewManager.initializeDataStructure();

		makeObservable(this, {
			scale: observable,
			featuresLoaded: observable,
			settings: observable,
			resetWindowsPositionsTrigger: observable,
			customData: observable,
			cell: observable,
			cells: observable,
			timeSettings: observable,
			getOrCreateCustomData: action,
			removeCustomData: action,
			restoreDeletedCustomData: action,
			persistableCustomData: computed,
			data: observable,
		});
	}

	private initializeDesignerData() {
		const storedData = sessionStorage.getItem('designerState');
		if (storedData) {
			sessionStorage.removeItem('designerState');

			const restoredData = JSON.parse(storedData);
			if ('extra' in restoredData) {
				if (restoredData.extra != null) {
					this.config.onExtraStateRestored(restoredData.extra);
				}
				delete restoredData.extra;
			}

			this.data = restoredData;
		} else {
			this.data = this.config.data ?? {
				xml: '',
				customData: [],
				views: [],
			};
		}
	}

	get isApplicationMode() {
		return this.legacyDesigner.config.mode == 'application';
	}

	get persistableCustomData() {
		const result = this.customData.filter((x) => !x.temporary);

		return result.map((x) => serialize(CustomDataEntry, toJS(x)));
	}

	addCustomData(newCustomData: CustomDataEntry[], triggerEvent: boolean = true) {
		const addedCustomData: CustomDataEntry[] = [];

		newCustomData.forEach((x) => {
			if (x.sourceView == null) {
				x.sourceView = this.viewManager.view;
			}

			if (x.cell != null) {
				addedCustomData.push(x);
				return;
			}

			const customData = new CustomDataEntry(x);

			iterateAllCells(this.graph, (cell: MxCell) => {
				const customDataId = cell.getAttribute('customDataId');
				if (customData.id == customDataId) {
					customData.cell = cell;
					return true;
				}
			});

			addedCustomData.push(customData);
		});

		this.customData.push(...addedCustomData);

		if (triggerEvent) {
			this.triggerCustomDataAdded(addedCustomData);
		}
	}

	init() {
		this.graph.getSelectionModel().addListener(MxEvent.CHANGE, this.onSelectionChanged);
		this.graph.getModel().addListener(MxEvent.NOTIFY, this.processExecuteEvent);

		this.mobx.reaction(
			() => this.scale,
			() => {
				if (this.scale / 100 != this.graph.view.scale) {
					this.graph.view.setScale(this.scale / 100);
				}
			},
		);

		this.graph.view.addListener(MxEvent.SCALE, () => {
			if (this.scale != this.graph.view.scale * 100) {
				this.scale = Math.round(this.graph.view.scale * 100);
			}
		});

		this.widgetsManager?.init();
	}

	onSelectionChanged = () => {
		this.cells = this.graph.getSelectionCells() ?? [];
		this.cell = this.cells.length > 0 ? this.cells[0] : null;
	};

	processExecuteEvent = async (graphModel: MxGraphModel, ev: MxEventObject) => {
		const newDataSources: CustomDataEntry[] = [];

		ev.properties.changes.forEach((change) => {
			if (isMxChildChange(change)) {
				iterateChildren(change.child, (cell: MxCell) => {
					if (!cell.getSource()) {
						cell.setSource(this.viewManager.view);
					}

					let customData = this.getCustomData(cell);

					//a cell is added
					if (change.previous == null) {
						this.viewManager.removeDeletedMark(cell);

						//means that we are either copying an existing cell or we are restoring a deleted cell with 'undo'
						// (deleted from current view but owned by another view otherwise custom data would also have been deleted)
						if (customData != null) {
							//dont remember how exactly that case can happen
							if (customData.cell == cell) return;

							//we are restoring delete cell with undo, dont need to do anything except assignig a cell
							if (customData.cell == null) {
								customData.cell = cell;
								newDataSources.push(customData);
							} else {
								//a cell is copied, we need to add a cy of custom data
								const newCustomData = copyViaSerializr(customData);
								newCustomData.id = newGuid();
								newCustomData.cell = cell;
								newCustomData.copiedFrom = customData;
								cell.setAttribute('customDataId', newCustomData.id);

								this.customData.push(newCustomData);
								newDataSources.push(newCustomData);
							}
						} else {
							//in case of 'undo' for a cell which was owned by current view
							customData = this.restoreDeletedCustomData(cell);
							if (customData == null) {
								this.searchForCustomDataInClipboard(cell);
							}

							if (customData != null) {
								customData.cell = cell;
								newDataSources.push(customData);
								this.customData.push(customData);
							}
						}
					} else if (change.parent == null) {
						//a cell is removed
						this.viewManager.markAsDeleted(cell);

						if (customData?.cell != null) {
							this.removeCustomData(customData);
						}
					}

					//the rest of the code is about handling custom data
					//but we need to mark cell as deleted even if it does not have custom data in case we are in alternative view
					//so it would not be copied from the basic view on next load
					// if (change.parent == null) {
					// 	this.viewsManager.markAsDeleted(cell)
					// } else {
					// 	this.viewsManager.removeDeletedMark(cell)
					// }

					// let customData = this.getCustomData(cell)
					//
					// if (customData == null) {
					// 	customData = this.searchForCustomDataInClipboard(cell)
					// }
					//
					// if(customData == null){
					// 	customData = this.searchForCustomDataInDeleted(cell)
					// }
					//
					// if (customData == null) {
					// 	return
					// }

					// if (change.previous == null) {
					// 	if (customData.cell == cell)
					// 		return
					//
					// 	//we are importing cell from another view
					// 	if (customData.cell == null) {
					// 		customData.cell = cell
					// 		newDataSources.push(customData)
					// 	} else {
					// 		//that might look odd that we look for a custom data for a cell and then add it again
					// 		//but this is for a scenario when cell is copied - we look for custom data with an id of existing cell
					// 		//then copy it under new id and assign to a new cell
					// 		let newCustomData = copyViaSerializr(customData)
					// 		newCustomData.id = newGuid()
					// 		newCustomData.cell = cell
					// 		cell.setAttribute("customDataId", newCustomData.id)
					//
					// 		this.customData.push(newCustomData)
					// 		newDataSources.push(newCustomData)
					// 	}
					//
					// } else if (change.parent == null) {
					// 	this.removeCustomData(customData)
					// }
				});
			}
		});

		if (newDataSources.length) {
			//this.customData.push(...newDataSources)

			this.triggerCustomDataAdded(newDataSources);
		}
	};

	removeCustomData(customData: CustomDataEntry) {
		//we delete custom data only if cell was created on the current view
		//so if you create for example a widget on a basic view and then delete it on an alt view
		//it still be functional on the basic view and others alt views
		if (this.viewManager.view == customData.sourceView) {
			const index = this.customData.indexOf(customData);

			if (index != -1) {
				this.customData.splice(index, 1);
				this.triggerCustomDataRemoved(customData);

				this.deletedCustomData.push(customData);
			}
		}

		this.triggerCustomDataRemoved(customData);

		customData.cell = null;
	}

	triggerCustomDataAdded = (customData: CustomDataEntry[]) => {
		this.dataSourcesManager.onCustomDataAdded(customData);
		this.widgetsManager.onCustomDataAdded(customData);
	};

	triggerCustomDataRemoved = (customData: CustomDataEntry) => {
		this.dataSourcesManager.onCustomDataRemoved(customData);
		this.widgetsManager.onCustomDataRemoved(customData);
	};

	getCustomData(cell: MxCell) {
		if (cell == null) return null;

		const customDataId = cell.getCustomDataId();

		return this.customData.find((x) => x.id == customDataId);
	}

	restoreDeletedCustomData(cell: MxCell) {
		if (cell == null) return null;

		const customDataId = cell.getCustomDataId();

		const index = this.deletedCustomData.findIndex((x) => x.id == customDataId);
		if (index == -1) return null;

		const customData = this.deletedCustomData[index];
		this.deletedCustomData.splice(index, 1);

		return customData;
	}

	searchForCustomDataInClipboard(cell: MxCell) {
		if (!Array.isArray(MxClipboard.customDataList)) {
			return null;
		}

		const customDataId = cell.getCustomDataId();
		return MxClipboard.customDataList.find((x) => x.id == customDataId);
	}

	getOrCreateCustomData(cell: MxCell) {
		let result = this.getCustomData(cell);

		if (result == null) {
			result = new CustomDataEntry({
				id: newGuid(),
				cell,
				sourceView: this.viewManager.view,
			});

			this.customData.push(result);
			cell.setAttribute('customDataId', result.id);
		}

		return result;
	}

	_onDestroyCallbacks: (() => void)[] = [];

	onDestroy(callback: () => void) {
		this._onDestroyCallbacks.push(callback);
	}

	onDestroyRemove(callback: () => void) {
		ArrayHelpers.remove(this._onDestroyCallbacks, callback);
	}

	switchToView = (viewName: string) => {
		if (this.viewManager.view == viewName) return;

		const builder = getUrlBuilderForCurrentHash();
		builder.add('view', viewName);

		const state = toJS(this.legacyDesigner.getForSaving()) as Record<string, any>;
		state.extra = this.config.getExtraStateForSwitchingView?.();

		sessionStorage.setItem('designerState', JSON.stringify(state));
		this.bypassDirtyCheck = true;

		NavigationStore.go(builder.build(), {
			navigationAction: NavigationAction.PopOne,
		});
	};

	showHelpText = (props: AntAlertProps) => {
		const [root, destroy, container] = createContainer();

		const designerContainer = this.legacyDesigner.containerBoundingRect;

		container.style.left = designerContainer.left + 50 + 'px';
		container.style.top = designerContainer.top + 50 + 'px';
		container.style.position = 'absolute';
		container.style.width = '500px';

		root.render(
			<AntAlert
				showIcon={true}
				type={'info'}
				closable={true}
				onClose={() => {
					destroy();
				}}
				{...props}
			/>,
		);

		this.onDestroy(() => {
			destroy();
		});
	};

	destroy() {
		this._onDestroyCallbacks.forEach((x) => x());
		this.mobx?.destroy();
		this.widgetsManager?.destroy();
		this.dataSourcesManager?.destroy();
		this.widgetWizardStore?.destroy();
	}
}
